/* eslint-disable max-len */
import { UseMutationResult } from '@tanstack/react-query';
import React from 'react';
import styled from 'styled-components';
import { Colors } from '.';
import { LFC } from '../Legacy';
import { Copy, Flexer, FlexerProps } from '../app/dashboard/elements/LayoutElements';
import { Icon } from '../concepts/resources/Icon';
import { SubmitterResult } from '../platform/Hook';
import { FadeOut } from './Transitions';

export type ResultInfoMode = 'positive' | 'negative';

const IconFrame = styled.div<{ mode: ResultInfoMode }>`
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    margin-right: .5rem;
    background: ${p => Colors[p.mode]};
    color: white;
    border-radius: 1000px;
`;

const CopyFrame = styled.div<{ mode: ResultInfoMode }>`
    flex: 1 1 auto;
    ${Copy} {
        color: ${p => Colors[p.mode]};
        font-weight: 400;
        margin: 0;
    }
`;

const Base: LFC<FlexerProps & { mode: ResultInfoMode, message: string }> = ({ mode, message, ...flexer }) =>
    <FadeOut after={5000}>
        <Flexer flex="row" center {...flexer}>
            <IconFrame mode={mode}>
                {mode === 'positive' && <Icon size="1" src="deqo://icons/hero/check" />}
                {mode === 'negative' && <Icon size="1" src="deqo://icons/hero/x" />}
            </IconFrame>
            <CopyFrame mode={mode}>
                <Copy>{message}</Copy>
            </CopyFrame>
        </Flexer>
    </FadeOut>;

export const ResultInfo = Object.assign(Base, { IconFrame, CopyFrame });

export const ErrorInfo = React.memo<FlexerProps & { error?: Error }>(({ error, ...flexer }) => {
    if (error) {
        return <ResultInfo key={error.message} mode="negative" message={error.message} {...flexer} />;
    }
    return null;
});

export const ResultInfoForSubmitter = React.memo<FlexerProps & { result?: SubmitterResult, onSuccess?: string }>(({ result, onSuccess, ...flexer }) => {
    if (result && !result.ok) {
        return <ResultInfo key={result.id} mode="negative" message={result.error?.message ?? 'Something went wrong...'} {...flexer} />;
    }
    if (result && result.ok && onSuccess) {
        return <ResultInfo key={result.id} mode="positive" message={onSuccess} {...flexer} />;
    }
    return null;
});

export const ResultInfoForMutation = React.memo<FlexerProps & { mutation?: UseMutationResult<any>, onSuccess?: string }>(({ mutation, onSuccess, ...flexer }) => {
    if (mutation && !mutation.isLoading && mutation.isError) {
        return <ResultInfo key={mutation.failureCount} mode="negative" message={(mutation.error as any)?.message ?? 'Something went wrong...'} {...flexer} />;
    }
    if (mutation && !mutation.isLoading && mutation.isSuccess && onSuccess) {
        return <ResultInfo key={`${mutation.isSuccess}`} mode="positive" message={onSuccess} {...flexer} />;
    }
    return null;
});

